/*
 * ssstufff custom behaviour
 * -------------------------
 * Dev-owned JS, version-controlled and reviewed through git. Loaded deferred from
 * layout/theme.liquid. Keep it dependency-free and namespaced — no globals beyond
 * what is documented here.
 */

/*
 * Keep the Kiwi Sizing "Size Chart" button alive across variant changes.
 *
 * Horizon re-renders the variant picker through the Section Rendering API on every
 * variant:update (assets/events.js dispatches it on document, and theme components
 * like assets/local-pickup.js rebind on the same event). That swap replaces the
 * <legend> which the Kiwi app injected its button into on page load. The app only
 * injects once, so the first time a shopper picks a size the Size Chart button
 * disappears and never comes back.
 *
 * Verified on the store rather than assumed: after a size click, .ks-chart-container
 * is gone from the DOM while the app's own <script> block is still present, and
 * calling the app's loader puts it straight back.
 *
 * This listens for the theme's event and asks the app to re-inject, but only when
 * the container has actually gone, so a variant change that leaves it intact costs
 * nothing.
 */
(() => {
  const CONTAINER_SELECTOR = '.ks-chart-container';
  let pending = false;

  const restoreSizeChart = () => {
    pending = false;

    // Still there — the swap left it alone, or a previous pass already restored it.
    if (document.querySelector(CONTAINER_SELECTOR)) return;

    // App absent on this page, or not booted yet. Nothing to re-inject.
    if (typeof window.loadKiwiSizingChart !== 'function') return;

    window.loadKiwiSizingChart();
  };

  document.addEventListener('variant:update', () => {
    if (pending) return;
    pending = true;

    // Two frames so Horizon has finished putting the new markup in place before we
    // decide whether the container is missing.
    requestAnimationFrame(() => requestAnimationFrame(restoreSizeChart));
  });
})();

/*
 * Open the desktop header dropdown when the cursor was already on the menu.
 *
 * Horizon opens submenus from on:pointerenter attributes on each
 * .menu-list__list-item, handled by one document-level listener that
 * assets/component.js registers when the deferred module runs. pointerenter only
 * fires when the cursor crosses into the element, so if the cursor is already
 * resting on a menu item when that listener appears (typical right after clicking
 * a nav link — the next page loads under a parked cursor), the enter is lost.
 * Moving inside the same item fires nothing; only crossing into a sibling does,
 * which is why the menu "unsticks" when you roll over another item.
 *
 * Reproduced deterministically on the live store: load with the cursor on
 * "Products", wiggle inside it (stays closed), cross to "Capsules" (opens).
 *
 * This watches pointermove, and when the cursor is over a closed item it re-fires
 * the theme's own activation event — the same synthetic-pointerenter trick
 * Horizon's #reconcilePointerTarget uses in assets/header-menu.js. The
 * aria-expanded guard means it fires at most once per missed activation (activate
 * flips it to "true" synchronously) and skips items with no submenu, which never
 * get the attribute.
 */
(() => {
  document.addEventListener(
    'pointermove',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const item = target.closest('header-menu .menu-list__list-item');
      if (!item) return;

      const link = item.querySelector('[ref="menuitem"]');
      if (!link || link.getAttribute('aria-expanded') !== 'false') return;

      item.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
    },
    { passive: true }
  );
})();
